import {Field} from "@progress/kendo-react-form"
import {Popup} from "@progress/kendo-react-popup"
import dayjs from "dayjs"
import {MDBCol, MDBRow} from "mdbreact"
import React, {useEffect} from "react"
import {useRef, useState} from "react"
import {BookingDetail, fetchPackageSlots} from "../../../containers/MotBooking/bookingSlice"
import {RootState} from "../../../rootReducer"
import {useAppDispatch, useAppSelector} from "../../../store"
import PackageSlotScheduler from "../../Shared/PackageSlotScheduler"
import TestTimePicker from "../controls/timePicker"
import TestDatePicker from "../controls/datepicker"
import {Loader} from "@progress/kendo-react-indicators"

const TestDateSlotSelection = ({
    booking,
    updateBookingDetail,
}: {
    booking: BookingDetail
    updateBookingDetail: (booking: BookingDetail) => void
}) => {
    const dispatch = useAppDispatch()
    const regionId = useAppSelector((state: RootState) => state.application.selectedRegion.regionId)
    const schedulesForMOT = useAppSelector((state: RootState) => state.application.schedulesForMOT)

    const gettingBookingSlots = useAppSelector((state: RootState) => state.containers.motBooking.gettingBookingSlots)
    const testPackages = useAppSelector((state: RootState) => state.containers.motBooking.dealerTestPackages)

    const loadingAvailabilityInterval = useAppSelector(
        (state: RootState) => state.containers.motBooking.loadingAvailabilityInterval,
    )
    const dayAvailabilityInterval = useAppSelector(
        (state: RootState) => state.containers.motBooking.dvsaDayAvailability,
    )

    const anchor = useRef<any>()
    const contentRef = useRef<any>()
    const blurTimeoutRef = useRef<any>()

    const [show, setShow] = useState(false)

    const onOpenSchPopover = () => {
        contentRef.current.focus()

        if (booking?.testPackageID && booking?.testPackageID > 0)
            dispatch(
                fetchPackageSlots(
                    regionId,
                    booking.dealerID,
                    booking.testPackageID,
                    dayjs(booking.testStart).format("YYYY-MM-DD"),
                ),
            )
    }

    const onFocusSchPopover = () => {
        clearTimeout(blurTimeoutRef.current)
    }

    const onBlurSchPopover = () => {
        clearTimeout(blurTimeoutRef.current)
        blurTimeoutRef.current = setTimeout(onBlurTimeout, 200)
    }

    const onBlurTimeout = () => {
        setShow(false)
    }

    const showSchTimeSlots = () => {
        setShow(!show)
    }

    const onTestSlotSelection = (start: Date, end: Date, selectedPackage: number, testFee: number, laneFee: number) => {
        const bkdt = {
            ...booking,
            testStart: start.toString(),
            testEnd: end.toString(),
            testPackageID: selectedPackage,
            testFee: testFee,
            laneFee: laneFee,
            total: testFee + laneFee,
        }
        updateBookingDetail(bkdt)
        onBlurSchPopover()
    }

    const checkStartSelection = (type: string, value: string) => {
        const {testStart: bkStart} = booking
        let testStartDate = dayjs(value)
        if (type === "date") {
            testStartDate = dayjs(value)
                .add(dayjs(bkStart).get("hour"), "hour")
                .add(dayjs(bkStart).get("minute"), "minute")
        }

        let testEndDate = testStartDate.add(booking.testPackage?.duration ?? 0, "m")
        const bkdt = {
            ...booking,
            testStart: testStartDate.toDate().toString(),
            testEnd: testEndDate.toDate().toString(),
        }
        updateBookingDetail(bkdt)
    }

    const getMinEndTime = (testStart: Date) => {
        if (dayAvailabilityInterval)
            return testStart < dayAvailabilityInterval?.start ? dayAvailabilityInterval?.start : testStart
        return testStart
    }

    const getMaxEndTime = (endTime: Date | undefined) => {
        return endTime
    }

    const [isValidTestDate, setIsValidTestDate] = useState<boolean>(true)

    const hasScheduleForDay =
        schedulesForMOT
            .map(x => dayjs(x.startDateTime).format("DD/MM/YYYY"))
            .indexOf(dayjs(booking.testStart).format("DD/MM/YYYY")) >= 0

    useEffect(() => {
        setIsValidTestDate(hasScheduleForDay)
    }, [hasScheduleForDay])

    return (
        <MDBRow>
            <MDBCol md="12">
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-end",
                    }}
                >
                    <Field
                        name={"bk1testDate"}
                        component={TestDatePicker}
                        val={new Date(booking.testStart)}
                        label={"Test Date"}
                        updateStateValue={(value: string) => {
                            checkStartSelection("date", value)
                        }}
                        disabled={
                            booking.bookingID > 0 ? booking.isBlocked != null && booking.isBlocked : false
                        }
                    />

                    <button
                        type="button"
                        className="k-button k-button-md k-button-rectangle k-button-flat k-button-flat-primary"
                        onClick={showSchTimeSlots}
                        ref={anchor}
                        title="Show bookings and available slots"
                        disabled={
                            !(
                                booking.dealerID > 0 &&
                                booking.testPackageID &&
                                booking.testPackageID > 0 &&
                                booking.testStart &&
                                isValidTestDate
                            ) || loadingAvailabilityInterval || ( booking.bookingID > 0 ? booking.isBlocked != null && booking.isBlocked : false)
                        }
                    >
                        {loadingAvailabilityInterval ? (
                            <Loader style={{width: "14px"}} size="small" />
                        ) : (
                            <span className="k-icon k-i-clock"></span>
                        )}
                    </button>
                </div>
                <Popup
                    className={"mt-3"}
                    anchor={anchor.current}
                    anchorAlign={{
                        horizontal: "left",
                        vertical: "bottom",
                    }}
                    popupAlign={{
                        horizontal: "right",
                        vertical: "center",
                    }}
                    show={show}
                    onOpen={onOpenSchPopover}
                    popupClass={"popup-content"}
                >
                    <div
                        style={{width: "calc(100vw - 200px)"}}
                        ref={contentRef}
                        tabIndex={0}
                        onFocus={onFocusSchPopover}
                        onBlur={onBlurSchPopover}
                    >
                        <PackageSlotScheduler
                            dealerId={booking.dealerID}
                            testPackageId={booking.testPackageID ?? 0}
                            testDate={dayjs(booking.testStart).toDate()}
                            isSchedulerLoading={gettingBookingSlots}
                            onTestSlotSelection={onTestSlotSelection}
                        />
                    </div>
                </Popup>
            </MDBCol>
            <MDBCol md="6">
                <Field
                    name={"testStart"}
                    component={TestTimePicker}
                    value={dayjs(booking.testStart).toDate()}
                    label={"Start"}
                    min={dayAvailabilityInterval?.start ?? undefined}
                    max={dayAvailabilityInterval?.end ?? undefined}
                    updateStateValue={(value: string) => {
                        checkStartSelection("time", value)
                    }}
                />
            </MDBCol>
            <MDBCol md="6">
                <Field
                    name={"testEnd"}
                    component={TestTimePicker}
                    value={dayjs(booking.testEnd).toDate()}
                    label={"End"}
                    min={getMinEndTime(dayjs(booking.testStart).toDate())}
                    max={getMaxEndTime(dayAvailabilityInterval?.end)}
                    updateStateValue={(value: string) => {
                        const bkdt = {...booking, testEnd: value}
                        updateBookingDetail(bkdt)
                    }}
                />
            </MDBCol>
        </MDBRow>
    )
}

export default TestDateSlotSelection
